<script setup lang="ts">
  import { computed, onMounted, reactive, useTemplateRef } from 'vue'
  import DiscordIcon from '~icons/fa7-brands/discord'
  import PatreonIcon from '~icons/fa7-brands/patreon'
  import TwitchIcon from '~icons/fa7-brands/twitch'
  import { useAuth } from '~renderer/composables/useAuth'
  import { SocialProviderId } from '~shared/types/auth'

  const props = defineProps<{
    mode?: 'link' | 'unlink'
    targetProviderId?: SocialProviderId
  }>()

  const emit = defineEmits<{
    close: []
  }>()

  const { accounts, hasLinkedAccounts, linkAccount, unlinkAccount } = useAuth()
  const rootEl = useTemplateRef<HTMLDivElement>('rootEl')
  const state = reactive({
    shake: true
  })

  const providers = computed(
    () =>
      [
        {
          id: 'discord',
          name: 'Discord',
          icon: DiscordIcon,
          isConnected: accounts.discord,
          colors: 'text-[#5865F2]',
        },
        {
          id: 'twitch',
          name: 'Twitch',
          icon: TwitchIcon,
          isConnected: accounts.twitch,
          colors: 'text-[#9146FF]',
        },
        {
          id: 'patreon',
          name: 'Patreon',
          icon: PatreonIcon,
          isConnected: accounts.patreon,
          colors: 'text-[#FF424D]',
        },
      ] as const,
  )

  const targetProvider = computed(() => providers.value.find((p) => p.id == props.targetProviderId))

  onMounted(() =>
    rootEl.value?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }),
  )
</script>

<template>
  <div
    v-if="targetProvider"
    class="w-full max-w-2xl rounded-xl border border-red-400/80 bg-purple-500 p-8 text-white shadow-2xl"
    ref="rootEl"
    :class="{ shake: state.shake }"
    @animationend="state.shake = false"
  >
    <div v-if="mode === 'link'" class="flex h-full flex-col">
      <h2 class="font-display text-center text-3xl font-bold">Link an Account?</h2>

      <!-- Input Section -->
      <div class="mt-6 mb-8 flex flex-col items-center gap-4 text-center">
        <p>Would you like to link your Turnip Exchange account to</p>
        <p>
          <span :class="[targetProvider.colors, 'size-5']"
            ><component :is="targetProvider.icon" class="inline"
          /></span>
          {{ targetProvider.name }}
        </p>
      </div>

      <!-- Action -->
      <div class="flex justify-center">
        <button
          @click="linkAccount(targetProvider.id)"
          class="cursor-pointer rounded-lg bg-green-400 px-8 py-2 font-bold text-black transition-colors hover:bg-green-500"
        >
          Link {{ targetProvider.name }}
        </button>
      </div>
    </div>

    <div v-else-if="mode === 'unlink' && !hasLinkedAccounts" class="flex h-full flex-col">
      <h2 class="font-display text-center text-3xl font-bold">Cannot Unlink Account</h2>

      <!-- Input Section -->
      <div class="mt-6 mb-8 flex flex-col items-center gap-4 text-center">
        <p>
          We cannot unlink your
          <span :class="[targetProvider.colors, 'size-4']"
            ><component :is="targetProvider.icon" class="inline"
          /></span>
          {{ targetProvider.name }} account.
        </p>
        <p>You must have at least one account linked.</p>
      </div>

      <!-- Action -->
      <div class="flex justify-center">
        <button
          @click="emit('close')"
          class="cursor-pointer rounded-lg bg-green-400 px-8 py-2 font-bold text-black transition-colors hover:bg-green-500"
        >
          Close
        </button>
      </div>
    </div>

    <div v-else-if="mode === 'unlink'" class="flex h-full flex-col">
      <h2 class="font-display text-center text-3xl font-bold">Unlink an Account?</h2>

      <!-- Input Section -->
      <div class="mt-6 mb-8 flex flex-col items-center gap-4 text-center">
        <p>Would you like to unlink your Turnip Exchange account from</p>
        <p>
          <span :class="[targetProvider.colors, 'size-5']"
            ><component :is="targetProvider.icon" class="inline"
          /></span>
          {{ targetProvider.name }}
        </p>
      </div>

      <!-- Action -->
      <div class="flex justify-center">
        <button
          @click="unlinkAccount(targetProvider.id)"
          class="cursor-pointer rounded-lg bg-green-400 px-8 py-2 font-bold text-black transition-colors hover:bg-green-500"
        >
          Unlink {{ targetProvider.name }}
        </button>
      </div>
    </div>
  </div>
</template>
